import { BarChart3, CalendarClock, LayoutDashboard, ListChecks, ScrollText, Search, Settings, ShieldCheck, Users, Wallet } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import AppShell, { type NavItem } from "@/layouts/AppShell";
import { kycService, walletService } from "@/services/apiServices";
import { usePlatformSettings } from "@/services/platformSettings";
import AdminTopSearch from "@/layouts/AdminTopSearch";

import { usePermission } from "@/context/PermissionContext";
import { getSession, isStaffSession } from "@/components/feature/auth/session";

const ROLE_DISPLAY_NAMES: Record<string, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  retailer_onboarding_executive: "Retailer Onboarding Executive",
  retailer_onboarding_executive_walletrequesthandler: "Retailer Onboarding Executive & Wallet Request Handler",
  wallet_request_handler: "Wallet Handler",
};

function getBreadcrumb(path: string): string {
  if (path === "/admin/dashboard") return "Dashboard";
  if (path.startsWith("/admin/mandates")) return "All Mandates";
  if (path.startsWith("/admin/retailers")) return "Retailers";
  if (path.startsWith("/admin/kyc")) return "KYC";
  if (path.startsWith("/admin/wallets")) return "Wallets";
  if (path.startsWith("/admin/installments")) return "Installments";
  if (path.startsWith("/admin/logs")) return "Logs";
  if (path.startsWith("/admin/reports")) return "Reports";
  if (path.startsWith("/admin/settings")) return "Settings";
  if (path.startsWith("/admin/profile")) return "Profile";
  return "Dashboard";
}

export default function AdminLayout() {
  const session = getSession();

  // If not logged in -> redirect to login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // If retailer -> redirect to retailer area (retailers cannot access admin tab)
  if (!isStaffSession(session)) {
    return <Navigate to="/retailer" replace />;
  }

  const location = useLocation();
  const platformSettings = usePlatformSettings();
  const { canView, role } = usePermission();
  const [pendingTopups, setPendingTopups] = useState<number>(0);
  const [pendingKyc, setPendingKyc] = useState<number>(0);

  const fetchBadgeCounts = useCallback(async () => {
    try {
      const [walletRes, kycRes] = await Promise.allSettled([
        walletService.getAdminSummary(),
        kycService.getAdminList({ page: 1, limit: 1 }),
      ]);

      if (walletRes.status === "fulfilled") {
        const stats = walletRes.value?.data?.stats;
        if (stats && typeof stats.pendingCount === "number") {
          setPendingTopups(stats.pendingCount);
        }
      }

      if (kycRes.status === "fulfilled") {
        const counts = kycRes.value?.data?.counts;
        if (counts && typeof counts.pending === "number") {
          setPendingKyc(counts.pending);
        }
      }
    } catch {
      // silently handle error
    }
  }, []);

  useEffect(() => {
    fetchBadgeCounts();

    const handleWalletUpdate = (e: Event) => {
      const custom = e as CustomEvent<{ pendingCount?: number }>;
      if (typeof custom.detail?.pendingCount === "number") {
        setPendingTopups(custom.detail.pendingCount);
      } else {
        fetchBadgeCounts();
      }
    };

    const handleKycUpdate = (e: Event) => {
      const custom = e as CustomEvent<{ pendingCount?: number }>;
      if (typeof custom.detail?.pendingCount === "number") {
        setPendingKyc(custom.detail.pendingCount);
      } else {
        fetchBadgeCounts();
      }
    };

    window.addEventListener("mandatepro_wallet_badge_updated", handleWalletUpdate);
    window.addEventListener("mandatepro_kyc_badge_updated", handleKycUpdate);
    window.addEventListener("focus", fetchBadgeCounts);

    const interval = setInterval(fetchBadgeCounts, 30000);

    return () => {
      window.removeEventListener("mandatepro_wallet_badge_updated", handleWalletUpdate);
      window.removeEventListener("mandatepro_kyc_badge_updated", handleKycUpdate);
      window.removeEventListener("focus", fetchBadgeCounts);
      clearInterval(interval);
    };
  }, [fetchBadgeCounts]);

  const navItems: NavItem[] = useMemo(() => {
    const list: { mod: string; item: NavItem }[] = [
      { mod: "dashboard", item: { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard } },
      { mod: "mandates", item: { to: "/admin/mandates", label: "All Mandates", icon: ListChecks } },
      { mod: "installments", item: { to: "/admin/installments", label: "Installments", icon: CalendarClock } },
      { mod: "retailers", item: { to: "/admin/retailers", label: "Retailers", icon: Users } },
     // { mod: "kyc", item: { to: "/admin/kyc", label: "KYC", icon: ShieldCheck, badge: pendingKyc } },
      { mod: "wallets", item: { to: "/admin/wallets", label: "Wallets", icon: Wallet, badge: pendingTopups } },
      { mod: "logs", item: { to: "/admin/logs", label: "Logs", icon: ScrollText } },
      { mod: "reports", item: { to: "/admin/reports", label: "Reports", icon: BarChart3 } },
      { mod: "settings", item: { to: "/admin/settings", label: "Settings", icon: Settings } },
    ];

    return list.filter((l) => canView(l.mod)).map((l) => l.item);
  }, [canView, pendingKyc, pendingTopups]);

  const roleLabel = ROLE_DISPLAY_NAMES[role] || `${platformSettings.name} Admin`;
  const displayName = session?.name || "Admin User";
  const userInitials = displayName
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "A";

  return (
    <AppShell
      navItems={navItems}
      user={{
        name: displayName,
        role: roleLabel,
        initials: userInitials,
      }}
      breadcrumb={getBreadcrumb(location.pathname)}
      adminBadge
      topbarExtra={<AdminTopSearch />}
    />
  );
}